import { useNavigate } from "@tanstack/react-router";
import { CheckIcon, LockIcon } from "lucide-react";
import { H1, SubText, Text } from "@/shared/ui/typography";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/shared/ui/dialog";
import { cn } from "@/shared/lib/utils";
import { useIsOwner } from "@/entities/user/hooks/use-is-owner";
import { useFetchMeQuery } from "@/widgets/app-header/api/fetch-me-query";
import { DisabledActionTooltip } from "@/shared/ui/disabled-action-tooltip";
import { useUpgradeDialogStore } from "../model/upgrade-dialog-store";
import { useTranslation, T } from "@/shared/i18n";
import { SUBSCRIPTION_SETUP_ROUTE } from "@/shared/lib/routes";
import { track } from "@/shared/lib/analytics";
import {
  usePricingTiers,
  type TranslatedPricingTier,
} from "@/entities/plan-price";
import { SALES_CONTACT_URL } from "@/shared/config/pricing";
import { isSelfServePlan, type PlanId } from "@/shared/config/term-pricing";

type PricingCardProps = {
  tier: TranslatedPricingTier;
  isCurrent: boolean;
  isLowerTier: boolean;
  isTrialEligible: boolean;
  isOwner: boolean;
  onSelect: () => void;
  onContactSales: () => void;
};

const PricingCard = ({
  tier,
  isCurrent,
  isLowerTier,
  isTrialEligible,
  isOwner,
  onSelect,
  onContactSales,
}: PricingCardProps) => {
  const { t } = useTranslation();
  const isTierDisabled = !isSelfServePlan(tier.id);

  return (
    <div
      className={cn(
        "rounded-lg p-4 border h-full flex flex-col",
        // The plan the company already has reads as a positive state, so it wins
        // over the muted treatment a Contact-Sales tier would otherwise get.
        isCurrent
          ? "border-primary bg-primary/5"
          : isTierDisabled
            ? "border-border bg-muted/50 opacity-75"
            : "border-border bg-card",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <H1 className="text-2xl">{tier.name}</H1>
        {isCurrent && (
          <Badge variant="blue">
            <T id="Current plan" />
          </Badge>
        )}
      </div>
      {tier.price && (
        <div className="flex items-baseline gap-1">
          <Text className="text-lg font-bold">{tier.price}</Text>
          <SubText className="text-muted-foreground">{tier.period}</SubText>
        </div>
      )}
      <SubText className="text-muted-foreground mt-2 mb-4">
        {tier.description}
      </SubText>
      <ul className="space-y-1.5 mb-5 flex-1">
        {tier.features.map((feature, index) => (
          <li key={index} className="flex items-start gap-1.5">
            <CheckIcon className="h-3.5 w-3.5 text-green-500 shrink-0 mt-0.5" />
            <SubText>{feature}</SubText>
          </li>
        ))}
      </ul>
      <SubText className="text-muted-foreground flex gap-1 mt-0.5 mb-4">
        <tier.icon className="h-4 w-4 text-primary shrink-0" />
        {tier.subtitle}
      </SubText>
      {isCurrent ? null : isTierDisabled ? (
        <Button variant="outline" className="w-full" asChild>
          <a
            href={SALES_CONTACT_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onContactSales}
          >
            <T id="Contact Sales" />
          </a>
        </Button>
      ) : isLowerTier ? null : (
        <DisabledActionTooltip
          show={!isOwner}
          message={t(
            "Only the Owner of this account can perform licence and billing actions. Contact your Owner to proceed.",
          )}
          className="w-full"
        >
          <Button onClick={onSelect} disabled={!isOwner} className="w-full">
            {isTrialEligible ? <T id="Start free trial" /> : <T id="Upgrade" />}
          </Button>
        </DisabledActionTooltip>
      )}
    </div>
  );
};

const useFeatureUnlockTitles = (): Record<string, string> => {
  const { t } = useTranslation();

  return {
    content_access: t("Unlock content access"),
    pdf_export: t("Unlock PDF export"),
    ai_assistant: t("Unlock AI assistant"),
    custom_workflows: t("Unlock custom workflows"),
    api_access: t("Unlock API access"),
    custom_views: t("Custom views limit reached"),
    users: t("Users limit reached"),
    tender_creation: t("Unlock tender creation"),
    bid_agenda: t("Unlock bid agenda"),
    following: t("Unlock following"),
    full_tender_history: t("Unlock full tender history"),
    full_organisation_history: t("Unlock full organisation history"),
    full_caselaw_history: t("Unlock full case law history"),
    organisation_insights: t("Organisation insights"),
    caselaw_access: t("Case law access"),
    tender_sharing: t("Unlock tender sharing"),
    contract_intelligence: t("Unlock contract intelligence"),
    procurement_calendar: t("Unlock TenderApp Horizon"),
    premium_template: t("Unlock this template"),
  };
};

const SALES_CONTACT_FEATURES = new Set([
  "organisation_insights",
  "caselaw_access",
]);

const useSalesContactDescription = (): Record<string, string> => {
  const { t } = useTranslation();

  return {
    organisation_insights: t(
      "upgrade_dialog_organisation_insights_description",
    ),
    caselaw_access: t("upgrade_dialog_caselaw_access_description"),
  };
};

type UpgradeDialogContentProps = {
  feature: string;
  companySlug: string;
  /** Closes after the user moved on: an upgrade or the checkout. */
  onClose: () => void;
  /** Closes because the user turned the offer down. */
  onDismiss: () => void;
};

const UpgradeDialogContent = ({
  feature,
  companySlug,
  onClose,
  onDismiss,
}: UpgradeDialogContentProps) => {
  const me = useFetchMeQuery();
  const navigate = useNavigate();
  const { isOwner } = useIsOwner();
  const allTiers = usePricingTiers();
  const featureUnlockTitles = useFeatureUnlockTitles();
  const salesDescriptions = useSalesContactDescription();
  const isSalesContact = SALES_CONTACT_FEATURES.has(feature);

  const featureTitle = featureUnlockTitles[feature] ?? feature;

  const company = me.companies.find((c) => c.slug === companySlug);
  const currentTierId = company?.hasActiveSubscription
    ? (company.subscriptionPlan?.toLowerCase() ?? null)
    : null;

  const currentTierIndex = currentTierId
    ? allTiers.findIndex((tier) => tier.id === currentTierId)
    : -1;

  // Only Start has a plan button, and only while the company has no active
  // subscription (otherwise Start is the current or a lower tier), so selecting
  // it always starts the checkout.
  const handleSelect = (tierId: PlanId) => {
    onClose();
    navigate({
      to: SUBSCRIPTION_SETUP_ROUTE,
      params: { companySlug },
      search: { plan: tierId },
    });
  };

  const trackContactSales = () =>
    track("billing.sales.contacted", { source: "upgrade_dialog", feature });

  return (
    <DialogContent
      className={isSalesContact ? "max-w-lg" : "max-w-5xl"}
      onPointerDownOutside={() => onDismiss()}
      onEscapeKeyDown={() => onDismiss()}
    >
      <DialogHeader className="items-center">
        <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-2">
          <LockIcon className="h-6 w-6 text-muted-foreground" />
        </div>
        <DialogTitle className="text-xl text-center">
          {featureTitle}
        </DialogTitle>
        {isSalesContact ? (
          <DialogDescription className="text-center">
            {salesDescriptions[feature]}
          </DialogDescription>
        ) : (
          <DialogDescription className="text-center">
            {isOwner ? (
              <T id="Upgrade your plan to unlock the full potential of TenderApp." />
            ) : (
              <T id="Only the Owner of this account can perform licence and billing actions. Contact your Owner to proceed." />
            )}
          </DialogDescription>
        )}
      </DialogHeader>

      {isSalesContact ? (
        <div className="flex flex-col items-center gap-3">
          <Button asChild>
            <a
              href={SALES_CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackContactSales}
            >
              <T id="Contact Sales" />
            </a>
          </Button>
          <Button variant="ghost" onClick={onDismiss}>
            <T id="Not now" />
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-stretch">
            {allTiers.map((tier, index) => (
              <PricingCard
                key={tier.id}
                tier={tier}
                isCurrent={tier.id === currentTierId}
                isLowerTier={index < currentTierIndex}
                isTrialEligible={!company?.hasUsedTrial}
                isOwner={isOwner}
                onSelect={() => handleSelect(tier.id)}
                onContactSales={trackContactSales}
              />
            ))}
          </div>

          <div className="flex justify-center">
            <Button variant="ghost" onClick={onDismiss}>
              {isOwner ? <T id="Not now" /> : <T id="Close" />}
            </Button>
          </div>
        </>
      )}
    </DialogContent>
  );
};

export const UpgradeDialog = () => {
  const { feature, companySlug, closeUpgradeDialog } = useUpgradeDialogStore();
  const isOpen = feature !== null && companySlug !== null;

  // An outside click or Escape reaches here twice: the content's own handler,
  // then onOpenChange. Read the live store so only the first one is counted.
  const dismiss = () => {
    const openFeature = useUpgradeDialogStore.getState().feature;
    if (openFeature) {
      track("billing.paywall.dismissed", { feature: openFeature });
    }
    closeUpgradeDialog();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && dismiss()}>
      {isOpen && (
        <UpgradeDialogContent
          feature={feature}
          companySlug={companySlug}
          onClose={closeUpgradeDialog}
          onDismiss={dismiss}
        />
      )}
    </Dialog>
  );
};
